/**
 * WS2 Phase 2a — hydration-window computation.
 *
 * The "window" is the locally-known visible slice we prioritise hydrating first
 * on a sync run: the currently-active chat plus the top-K most-recently-active
 * chats PER list type (AI / p2p / group). Returned as an ORDERED, de-duplicated
 * list of chat ids (highest priority first).
 *
 * Why per-list-type (not a global top-N): the three lists are separate tabs. A
 * user sitting on the AI tab must get AI-window freshness even though AI chats
 * are globally the least-recently-active (each AI conversation is a fresh group
 * chat, so they concentrate at the traversal tail — the incident's primary
 * victim). Per-type K slots guarantee the AI tab priority regardless of global
 * recency. This is the F4 property.
 *
 * Cost is STRICTLY O(window), NOT O(account) (review finding F10): each type is
 * read with an indexed reverse cursor + a bounded `.limit(...)`, never a
 * load-and-sort or a full-table scan of all rows.
 *   - AI:    `[hubId+lastMessageTimestamp]`  (hubId = CHAT_HUB_ID).
 *   - p2p:   `[type+lastMessageTimestamp]`   (p2p rows never carry a hubId).
 *   - group: `[type+lastMessageTimestamp]` — type=GROUP ALSO matches AI chats
 *            (AI chats are group chats), and group rows have hubId=null (not
 *            indexable), so we read a bounded over-fetch of the newest type=GROUP
 *            rows and drop the hub (AI) ones. Bounded ⇒ still O(window). In the
 *            pathological "hundreds of very recent AI chats" case a few real
 *            groups may fall outside the over-fetch and miss the window; that is
 *            an acceptable priority miss (they still hydrate in the fallback
 *            global pass), never a correctness issue.
 *
 * Pinned chats are deliberately NOT scanned here (review finding #2). `isPinned`
 * is a boolean, and IndexedDB cannot index boolean keys, so a `.equals(true)`
 * query is impossible — the only way to gather pinned chats would be an
 * O(account) full-table scan on every windowed run, on exactly the heavy
 * accounts this targets, breaking the O(window) invariant. Coverage without it:
 * a recently-active pinned chat is already in its type's top-K; the currently
 * open pinned chat is the active chat (included below); and any stale-pinned chat
 * still hydrates in the fallback global pass right after the window. Prioritising
 * stale-pinned chats would require an indexable pin representation (a data-model
 * change) — out of scope for this pure-ordering phase.
 */

import Dexie from "dexie";

import { db } from "@/indexedDB/db";
import { ChatType } from "@/server/types/chat";
import { CHAT_HUB_ID } from "@/config/constants";
import { useNavigationState } from "@/zustand/NavigationState";

/** Per-list-type window size (viewport + overscan). Telemetry-tunable. */
export const WINDOW_K = 20;

/**
 * Multiple of K of the newest type=GROUP rows to read before filtering out AI
 * (hub) chats — keeps the group read bounded (O(window)) while tolerating some
 * interleaved AI chats at the top of the type=GROUP index.
 */
const GROUP_OVERFETCH = 4;

/**
 * Compute the hydration window from local Dexie. Returns an ordered, de-duped
 * list of chat ids: active chat → interleaved top-K per type.
 */
export async function computeHydrationWindow(k = WINDOW_K): Promise<string[]> {
  const [aiTop, p2pTop, groupTopRows] = await Promise.all([
    // AI chats — indexed reverse cursor on [hubId+lastMessageTimestamp].
    db.chats
      .where("[hubId+lastMessageTimestamp]")
      .between([CHAT_HUB_ID, Dexie.minKey], [CHAT_HUB_ID, Dexie.maxKey])
      .reverse()
      .limit(k)
      .primaryKeys(),
    // p2p chats — indexed reverse cursor on [type+lastMessageTimestamp].
    db.chats
      .where("[type+lastMessageTimestamp]")
      .between([ChatType.P2P, Dexie.minKey], [ChatType.P2P, Dexie.maxKey])
      .reverse()
      .limit(k)
      .primaryKeys(),
    // group chats — bounded over-fetch of newest type=GROUP, filter AI out below.
    db.chats
      .where("[type+lastMessageTimestamp]")
      .between([ChatType.GROUP, Dexie.minKey], [ChatType.GROUP, Dexie.maxKey])
      .reverse()
      .limit(k * GROUP_OVERFETCH)
      .toArray(),
  ]);

  const groupTop = groupTopRows
    .filter((c) => !c.hubId)
    .slice(0, k)
    .map((c) => c.id);

  const activeChatId = useNavigationState.getState().activeChatId;

  const ordered: string[] = [];
  const seen = new Set<string>();
  const push = (id: string | null | undefined) => {
    if (id && !seen.has(id)) {
      seen.add(id);
      ordered.push(id);
    }
  };

  // 1. active chat first (Slack "active channel first"). Pinned chats are NOT
  //    scanned here — see the module comment (boolean isn't IndexedDB-indexable;
  //    an O(account) scan would break the O(window) invariant).
  push(activeChatId);
  // 2. interleave the three type-windows so no single tab starves the others
  const maxLen = Math.max(aiTop.length, p2pTop.length, groupTop.length);
  for (let i = 0; i < maxLen; i++) {
    push(aiTop[i] as string | undefined);
    push(p2pTop[i] as string | undefined);
    push(groupTop[i]);
  }

  return ordered;
}
